/**
 * Squad built-in tools client — the catalog of function tools an agent can call
 * (`http_request`, `email`, `memory`, `web_search`, …) and the per-agent
 * enable/config set. Distinct from MCP servers (see `mcpServers.ts`): tools are
 * a static backend catalog, MCP servers are remote protocol endpoints.
 *
 *   - Catalog:    `GET /api/tools/`
 *   - Per-agent:  `GET /api/agents/{id}/tools/`  ·  `PUT /api/agents/{id}/tools/`
 */

import { jsonOrThrow, squadFetch } from './squadAgent'

/** BYOK provider attached to a tool (e.g. Firecrawl). */
export type SquadToolProvider = {
  slug: string
  name: string
  icon_url?: string
}

/** A built-in tool from the static catalog (`GET /api/tools/`). */
export type SquadToolCatalogEntry = {
  key: string
  name: string
  description?: string
  category?: string
  config_schema?: Record<string, unknown>
  provider?: SquadToolProvider
}

/** A tool enabled on an agent (`GET /api/agents/{id}/tools/`). */
export type SquadAgentTool = {
  id: number
  tool_key: string
  config: Record<string, unknown>
}

type Paginated<T> = { results?: T[] }

const unwrap = <T>(data: T[] | Paginated<T>): T[] =>
  Array.isArray(data) ? data : (data.results ?? [])

/** The static catalog of built-in tools available to enable. */
export const listToolCatalog = async (
  appToken: string
): Promise<SquadToolCatalogEntry[]> => {
  const response = await squadFetch('/api/tools/', { method: 'GET' }, appToken)
  const data = await jsonOrThrow<
    SquadToolCatalogEntry[] | Paginated<SquadToolCatalogEntry>
  >(response)

  return unwrap(data)
}

/** The tools currently enabled on an agent (with their config). */
export const listAgentTools = async (
  appToken: string,
  agentId: string
): Promise<SquadAgentTool[]> => {
  const response = await squadFetch(
    `/api/agents/${agentId}/tools/`,
    { method: 'GET' },
    appToken
  )
  const data = await jsonOrThrow<SquadAgentTool[] | Paginated<SquadAgentTool>>(
    response
  )

  return unwrap(data)
}

/** Tool entry for the replace-all write. */
export type AgentToolWrite = {
  tool_key: string
  config?: Record<string, unknown>
}

/**
 * Replace the agent's full tool set (`PUT /api/agents/{id}/tools/`). The backend
 * diffs against the current rows and applies create/update/delete atomically.
 */
export const setAgentTools = async (
  appToken: string,
  agentId: string,
  tools: AgentToolWrite[]
): Promise<SquadAgentTool[]> => {
  const response = await squadFetch(
    `/api/agents/${agentId}/tools/`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tools })
    },
    appToken
  )
  const data = await jsonOrThrow<SquadAgentTool[] | Paginated<SquadAgentTool>>(
    response
  )

  return unwrap(data)
}
